import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../utils/apiConfig';
import { Trophy, Calendar, MapPin, Users, Loader2, ChevronRight } from 'lucide-react';
import { fileUrl, statusLabel, cAuth } from '../../components/events/eventUtils';
import CustomerFooter from '../../components/CustomerFooter';

export default function PlayerEventsPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/events/public`, cAuth())
      .then((r) => setEvents(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50 to-fuchsia-50 pb-24">
      <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 px-5 pt-8 pb-10 text-white">
        <div className="flex items-center gap-2">
          <Trophy className="w-6 h-6" />
          <h1 className="text-2xl font-bold">Events & Tournaments</h1>
        </div>
        <p className="text-purple-100 text-sm mt-1">Register for upcoming MAKS tournaments</p>
      </div>

      <div className="px-4 -mt-5 space-y-4">
        {loading ? (
          <div className="text-center py-16 text-slate-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
        ) : events.length === 0 ? (
          <div className="bg-white rounded-2xl p-10 text-center text-slate-400 shadow-sm" data-testid="player-events-empty">
            <Trophy className="w-10 h-10 mx-auto mb-3 opacity-40" />
            No open events right now. Check back soon!
          </div>
        ) : events.map((ev) => (
          <button
            key={ev.id}
            onClick={() => navigate(`/events/${ev.id}`)}
            className="block w-full text-left bg-white rounded-2xl shadow-md overflow-hidden active:scale-[0.99] transition-transform"
            data-testid={`player-event-${ev.id}`}
          >
            {ev.banner_file_id ? (
              <img src={fileUrl(ev.banner_file_id)} alt={ev.name} className="h-36 w-full object-cover" />
            ) : (
              <div className="h-36 w-full bg-gradient-to-br from-violet-200 to-fuchsia-200 flex items-center justify-center">
                <Trophy className="w-10 h-10 text-violet-500" />
              </div>
            )}
            <div className="p-4">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-bold text-lg text-slate-900">{ev.name}</h3>
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold shrink-0">{statusLabel(ev.status)}</span>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mt-2">
                {ev.venue && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {ev.venue}</span>}
                {ev.start_date && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {ev.start_date}</span>}
                <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {ev.total_registrations || 0} joined</span>
              </div>
              <div className="flex items-center justify-between mt-3">
                <span className="text-xs text-slate-500">{(ev.categories || []).length} categories</span>
                <span className="flex items-center gap-1 text-sm font-semibold text-violet-600">Register <ChevronRight className="w-4 h-4" /></span>
              </div>
            </div>
          </button>
        ))}
      </div>
      <CustomerFooter />
    </div>
  );
}
